import React, { useMemo } from "react";
import {
  AlertTriangle,
  ExternalLink,
  Info,
  TableProperties,
  Users
} from "lucide-react";
import { CollapsibleSection } from "../AIInsights/CollapsibleSection";
import { useAppBaseUrl } from "../../../../../hooks/useAppBaseUrl";
import { useShowAICoPilot } from "../../../../../hooks/useShowAICoPilot";
import useWorkspaceContext from "../../../../../hooks/useWorkspaceContext";
import { isNativeTab } from "../../../../../utils/environment";
import { openCollinsAdminLink } from "../../../../../utils/navigation/openCollinsAdminLink";
import {
  ContextualHelpResult,
  getContextualHelp,
  getDefaultUnassignedSuggestion
} from "../../../../../services/ai/SpaceOptimiserAI";
import { ProcessedScanResult, ScanResultType } from "../SpaceScanner.types";
import {
  getAreaNames,
  getBookingCustomerName,
  getReasonText
} from "./SpaceScannerStage2Controller";

type SpaceScannerUnassignedSectionProps = {
  unassignedResults: ProcessedScanResult[];
  venueConfig?: { maxTableCapacity?: number };
};

type UnassignedGroup = {
  type: ScanResultType;
  results: ProcessedScanResult[];
  icon: React.ReactNode;
  suggestion: string;
  contextualHelp?: ContextualHelpResult;
};

/**
 * Returns an appropriate icon for the unassigned reason type
 */
const getIcon = (type: ScanResultType): React.ReactNode => {
  switch (type) {
    case "unassigned_no_tables_configured":
      return (
        <TableProperties className="size-4 text-[var(--progressMed,#f59e0b)]" />
      );
    case "unassigned_table_too_small":
      return <Users className="size-4 text-[var(--progressMed,#f59e0b)]" />;
    case "unassigned_no_availability":
      return <Info className="size-4 text-[var(--progressMed,#f59e0b)]" />;
    case "unassigned_rule_conflict":
    default:
      return (
        <AlertTriangle className="size-4 text-[var(--progressMed,#f59e0b)]" />
      );
  }
};

/**
 * Component to display unassigned bookings grouped by reason with actionable suggestions
 */
export const SpaceScannerUnassignedSection = ({
  unassignedResults,
  venueConfig
}: SpaceScannerUnassignedSectionProps) => {
  const appBaseUrl = useAppBaseUrl();
  const showAICoPilot = useShowAICoPilot();
  const workspace = useWorkspaceContext(isNativeTab());

  const groupedResults = useMemo(() => {
    const groups: Map<ScanResultType, ProcessedScanResult[]> = new Map();

    for (const result of unassignedResults) {
      const existing = groups.get(result.result) ?? [];
      groups.set(result.result, [...existing, result]);
    }

    const groupedArray: UnassignedGroup[] = [];
    groups.forEach((results, type) => {
      const contextualHelp = showAICoPilot
        ? getContextualHelp(results, venueConfig)
        : undefined;

      groupedArray.push({
        type,
        results,
        icon: getIcon(type),
        suggestion: showAICoPilot
          ? (contextualHelp?.suggestion ?? getDefaultUnassignedSuggestion(type))
          : getDefaultUnassignedSuggestion(type),
        contextualHelp
      });
    });

    return groupedArray;
  }, [unassignedResults, showAICoPilot, venueConfig]);

  if (unassignedResults.length === 0) {
    return null;
  }

  return (
    <CollapsibleSection
      icon={
        <AlertTriangle className="size-5 flex-shrink-0 text-[var(--progressMed,#f59e0b)]" />
      }
      title={`${unassignedResults.length} Booking${unassignedResults.length !== 1 ? "s" : ""} Could Not Be Assigned`}
      ariaLabel="Unassigned bookings"
      sectionClassName="mb-4 rounded-lg border border-[var(--borderColor)] bg-[var(--pageBackgroundSecondary)] p-3"
    >
      {groupedResults.map((group) => (
        <div
          key={group.type}
          className="rounded-md bg-[var(--panelBackground)] p-2.5"
        >
          <div className="mb-2 flex items-center gap-2">
            {group.icon}
            <span className="text-[13px] font-medium text-[var(--primaryColor)]">
              {getReasonText(group.type)} ({group.results.length})
            </span>
          </div>

          <ul
            className="mb-2 ml-6 list-disc text-xs text-[var(--secondaryColor)]"
            aria-label={`Bookings with ${getReasonText(group.type)}`}
          >
            {group.results.map((result) => (
              <li key={result.booking._id} className="mb-1 last:mb-0">
                <span className="font-medium">
                  {getBookingCustomerName(result.booking)}
                </span>{" "}
                ({result.booking.num_people} guest
                {result.booking.num_people !== 1 ? "s" : ""})
                {result.booking.time && (
                  <span className="text-[var(--tertiaryColor,#9ca3af)]">
                    {" "}
                    at {result.booking.time}
                  </span>
                )}
                {getAreaNames(result.previousAreas) && (
                  <span className="text-[var(--tertiaryColor,#9ca3af)]">
                    {" "}
                    - Currently: {getAreaNames(result.previousAreas)}
                  </span>
                )}
              </li>
            ))}
          </ul>

          <p className="ml-6 text-xs italic text-[var(--secondaryColor)]">
            {group.suggestion}
          </p>

          {group.contextualHelp?.actionLabel && (
            <div className="ml-6 mt-1.5">
              {showAICoPilot && group.contextualHelp.actionPath ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[var(--accent-color,#3b82f6)] hover:underline focus:outline-none focus:ring-2 focus:ring-ring"
                  onClick={() => {
                    const url = `${appBaseUrl}${group.contextualHelp!.actionPath}`;
                    openCollinsAdminLink(url, appBaseUrl, workspace);
                  }}
                >
                  <ExternalLink className="size-3" aria-hidden="true" />
                  {group.contextualHelp.actionLabel}
                </button>
              ) : (
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium text-[var(--accent-color,#3b82f6)]"
                  role="note"
                  aria-label={group.contextualHelp.actionLabel}
                >
                  <ExternalLink className="size-3" aria-hidden="true" />
                  {group.contextualHelp.actionLabel}
                </span>
              )}
            </div>
          )}
        </div>
      ))}
    </CollapsibleSection>
  );
};
